import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Col,
  Container,
  Row,
  Button,
} from "reactstrap";
import * as FlexmonsterReact from "react-flexmonster";
import moment from "moment";
import { useAuth } from "../../../providers/authProvider";
import { reportsApi } from "../../../services/reportsServices";
import Loader from "../../../components/Loader";
import InformationModal from "../../../components/InformationModal";
import CustomPeriodSelector from "../../../components/CustomPeriodSelector";
import { utils } from "../../../utils/utils";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PeriodTypeSelector from "../../../components/PeriodTypeSelector";

const columns = {
  "Job Number": {
    type: "string",
  },
  "Project Number": {
    type: "string",
  },
  Customer: {
    type: "string",
  },
  "Customer PO": {
    type: "string",
  },
  Description: {
    type: "string",
  },
  "Product Type": {
    type: "string",
  },
  "Contract Date": {
    type: "string",
  },
  "Est. Ship Date": {
    type: "string",
  },
  "Total Manhours": {
    type: "number",
  },
  "Contract Amount": {
    type: "number",
  },
};

const report = {
  dataSource: {
    data: [columns],
  },
  formats: [
    {
      name: "",
      thousandsSeparator: ",",
      decimalPlaces: 2,
    },
  ],
  slice: {
    rows: [
      {
        uniqueName: "Job Number",
      },
      {
        uniqueName: "Project Number",
      },
      {
        uniqueName: "Customer",
      },
      {
        uniqueName: "Customer PO",
      },
      {
        uniqueName: "Description",
      },
      {
        uniqueName: "Product Type",
      },
      {
        uniqueName: "Contract Date",
      },
      {
        uniqueName: "Est. Ship Date",
      },
      {
        uniqueName: "Total Manhours",
      },
      {
        uniqueName: "Contract Amount",
      },
    ],
    expands: {
      expandAll: true,
    },
  },
  options: {
    configuratorActive: false,
    grid: {
      type: "flat",
      showTotals: "off",
      title: "Sales Report",
      grandTotalsPosition: "bottom",
    },
  },
};

const TYPE_CUSTOM = "TYPE_CUSTOM";
const DATE_FIELDS_TO_FORMAT = ["Contract Date", "Est. Ship Date"];

const SalesReport = () => {
  const [authContext] = useAuth();
  const pivot = useRef(null);

  const [periodType, setPeriodType] = useState({
    value: TYPE_CUSTOM,
    label: "Date Range",
  });

  const [customStartDate, setCustomStartDate] = useState(
    moment().startOf("isoWeek")
  );
  const [customEndDate, setCustomEndDate] = useState(moment().endOf("isoWeek"));
  const [loading, setLoading] = useState();
  const [refresh, setRefresh] = useState();
  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    title: "",
    body: "",
  });

  useEffect(() => {
    setLoading(true);
    const condition = {};
    if (periodType.value === TYPE_CUSTOM) {
      condition.startDate = moment(customStartDate).format("YYYY-MM-DD");
      condition.endDate = moment(customEndDate).format("YYYY-MM-DD");
    }
    reportsApi
      .salesAndJobBook(condition)
      .then((result) => {
        result = utils.formatDatesToObjectsInArray(
          result,
          DATE_FIELDS_TO_FORMAT
        );
        setLoading(false);
        if (pivot) {
          pivot.current.flexmonster.updateData({
            data: [columns, ...result],
          });
        }
      })
      .catch(() => {
        setLoading(false);
        return setInformationModal({
          isOpen: true,
          title: "Error",
          body: "There was an error with your request.",
        });
      });
  }, [
    pivot,
    customEndDate,
    customStartDate,
    authContext.currentUser,
    refresh,
    periodType,
  ]);

  return (
    <Container fluid className="d-flex flex-column flex-grow-1">
      <Row className="flex-grow-1">
        <Col className="d-flex flex-column">
          <Card className="flex-grow-1">
            <CardHeader className="d-flex align-items-center justify-content-between">
              <h3 className="mb-0">Sales Report</h3>
              <div className="d-flex align-items-center">
                {loading ? (
                  <div className="min-width-50">
                    <Loader size="sm" className="mr-3" />
                  </div>
                ) : null}
                <PeriodTypeSelector
                  periodType={periodType}
                  setPeriodType={setPeriodType}
                />
                {periodType.value === TYPE_CUSTOM ? (
                  <CustomPeriodSelector
                    defaultEndDate={customEndDate}
                    defaultStartDate={customStartDate}
                    onSubmit={(startDate, endDate) => {
                      setCustomStartDate(startDate);
                      setCustomEndDate(endDate);
                    }}
                  />
                ) : null}
                <Button
                  size="sm"
                  className="rounded-circle custom-rounded-button d-flex ml-3"
                  color="secondary"
                  onClick={() => setRefresh(!refresh)}
                >
                  <FontAwesomeIcon icon={faSync} size="sm" />
                </Button>
              </div>
            </CardHeader>
            <CardBody className="d-flex flex-column flex-grow-1">
              <div className="rounded border height-100">
                <FlexmonsterReact.Pivot
                  key={customStartDate + "-" + customEndDate}
                  ref={pivot}
                  licenseKey={process.env.REACT_APP_FLEX_M_KEY}
                  toolbar={true}
                  height="100%"
                  report={report}
                  beforetoolbarcreated={(toolbar) =>
                    utils.customizeToolbarReport(toolbar, pivot, "sales_report")
                  }
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {informationModal.isOpen ? (
        <InformationModal
          {...informationModal}
          onClose={() => setInformationModal({ isOpen: false })}
        />
      ) : null}
    </Container>
  );
};

export default SalesReport;
